import React, { useEffect, useState } from 'react';
import api, { isSupervisorRole, getCurrentUser } from '../api/expoApi';

export default function PaymentSheets() {
    const user = getCurrentUser();
    const isSupervisor = isSupervisorRole(user?.role);
    const [sheets, setSheets] = useState([]);
    const [override, setOverride] = useState(false);
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [newLine, setNewLine] = useState({ tracking_id: '', line_type: 'COD_COLLECTED', amount: '', reference: '' });
    const [msg, setMsg] = useState('');

    const load = async () => {
        try {
            const params = override ? { supervisor_override: 'true' } : {};
            const r = await api.get('/payment-sheets', { params });
            setSheets(r.data?.data || []);
        } catch (e) { console.error(e); }
    };

    const loadDetail = async (id) => {
        try {
            const params = override ? { supervisor_override: 'true' } : {};
            const r = await api.get(`/payment-sheets/${id}`, { params });
            setDetail(r.data?.data);
        } catch (e) { setMsg(e.response?.data?.message || 'Error'); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [override]);
    useEffect(() => { if (selected) loadDetail(selected); /* eslint-disable-next-line */ }, [selected, override]);

    const createSheet = async () => {
        try {
            const r = await api.post('/payment-sheets', {});
            setMsg(`Created ${r.data?.data?.sheet_number}`);
            load();
        } catch (e) { setMsg(e.response?.data?.message || 'Error'); }
    };

    const addLine = async () => {
        try {
            await api.post(`/payment-sheets/${selected}/lines`, newLine);
            setNewLine({ tracking_id: '', line_type: 'COD_COLLECTED', amount: '', reference: '' });
            loadDetail(selected);
        } catch (e) { setMsg(e.response?.data?.message || 'Error'); }
    };

    const closeSheet = async () => {
        if (!window.confirm('Close this sheet? You won\'t be able to edit it after.')) return;
        try {
            await api.post(`/payment-sheets/${selected}/close`);
            setMsg('Sheet closed');
            loadDetail(selected); load();
        } catch (e) { setMsg(e.response?.data?.message || 'Error'); }
    };

    const isMine = detail?.owner_id === user?.id;
    const canEdit = isMine && detail?.status === 'OPEN';

    return (
        <div style={{ padding: 24 }}>
            <h1>Payment Sheets</h1>
            <div style={{ background: '#fff3cd', padding: 12, borderRadius: 4, marginBottom: 16, fontSize: '.9rem' }}>
                Payment sheets are <strong>owner-isolated</strong> — you only see your own.
                {isSupervisor && ' As a supervisor, you can toggle override below to view all sheets (audit-logged).'}
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
                <button onClick={createSheet}>+ New Sheet</button>
                {isSupervisor && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />
                        Supervisor override (view all)
                    </label>
                )}
                {msg && <span style={{ color: '#198754' }}>{msg}</span>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 24 }}>
                <div>
                    <h3>Sheets</h3>
                    {sheets.map((s) => (
                        <div key={s.sheet_id} onClick={() => setSelected(s.sheet_id)} style={{
                            padding: 10, marginBottom: 6, borderRadius: 4, cursor: 'pointer',
                            background: selected === s.sheet_id ? '#0d6efd' : '#fff',
                            color: selected === s.sheet_id ? '#fff' : '#333',
                            border: '1px solid #ddd'
                        }}>
                            <strong>{s.sheet_number}</strong>
                            <div style={{ fontSize: '.85rem', opacity: .8 }}>
                                {s.sheet_date} · {s.status} · PKR {Number(s.total_collected).toLocaleString()}
                                {override && s.owner_id !== user?.id && <span> · {s.owner_name}</span>}
                            </div>
                        </div>
                    ))}
                </div>

                <div>
                    {detail ? (
                        <>
                            <div style={{ background: '#f8f9fa', padding: 12, borderRadius: 6, marginBottom: 12 }}>
                                <h3 style={{ margin: 0 }}>{detail.sheet_number}</h3>
                                <div>Date: {detail.sheet_date} · Status: <strong>{detail.status}</strong></div>
                                <div>Collected: PKR {Number(detail.total_collected).toLocaleString()}</div>
                                <div>Disbursed: PKR {Number(detail.total_disbursed).toLocaleString()}</div>
                                <div>Net: <strong>PKR {(Number(detail.total_collected) - Number(detail.total_disbursed)).toLocaleString()}</strong></div>
                                {!isMine && override && <div style={{ color: '#dc3545' }}>(Owned by another user — override active, audit-logged)</div>}
                            </div>

                            {canEdit && (
                                <div style={{ background: '#f8f9fa', padding: 12, borderRadius: 6, marginBottom: 12 }}>
                                    <h4>Add Line</h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                                        <input placeholder="Tracking ID" value={newLine.tracking_id} onChange={(e) => setNewLine({...newLine, tracking_id: e.target.value})} />
                                        <select value={newLine.line_type} onChange={(e) => setNewLine({...newLine, line_type: e.target.value})}>
                                            <option value="COD_COLLECTED">COD Collected</option>
                                            <option value="REFUND">Refund</option>
                                            <option value="EXPENSE">Expense</option>
                                            <option value="OTHER">Other</option>
                                        </select>
                                        <input type="number" step="0.01" placeholder="Amount" value={newLine.amount} onChange={(e) => setNewLine({...newLine, amount: e.target.value})} />
                                        <input placeholder="Reference" value={newLine.reference} onChange={(e) => setNewLine({...newLine, reference: e.target.value})} />
                                    </div>
                                    <button onClick={addLine} style={{ marginTop: 6 }}>Add Line</button>
                                </div>
                            )}

                            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                                <thead><tr><th>Tracking</th><th>Type</th><th>Amount</th><th>Reference</th></tr></thead>
                                <tbody>
                                    {(detail.lines || []).map((l) => (
                                        <tr key={l.line_id}>
                                            <td>{l.tracking_id}</td>
                                            <td>{l.line_type}</td>
                                            <td style={{ color: ['REFUND', 'EXPENSE'].includes(l.line_type) ? '#dc3545' : '#198754' }}>
                                                PKR {Number(l.amount).toLocaleString()}
                                            </td>
                                            <td>{l.reference}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {canEdit && (
                                <button onClick={closeSheet} style={{ marginTop: 12, padding: '10px 20px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 4 }}>
                                    Close Sheet
                                </button>
                            )}
                        </>
                    ) : (
                        <p style={{ color: '#888' }}>Select a sheet to view details.</p>
                    )}
                </div>
            </div>
        </div>
    );
}
